// FUNCTIONALITY-115B / CATALOG-SYNC-119A — single effective-price resolver (customer surfaces).
import type { CatalogV2Product } from "./catalog-v2-types.js";

export interface SalePricingFields {
  regular_price: number | null;
  sale_price: number | null;
  sale_starts_at: string | null;
  sale_ends_at: string | null;
  promotion_id: string | null;
  promotion_status: string | null;
  price_updated_at: string | null;
  source_revision: string | null;
  currency: string;
}

export interface EffectivePriceResult {
  regular_price: number;
  effective_price: number;
  discount_amount: number;
  discount_percent: number;
  promotion_active: boolean;
  promotion_end_at: string | null;
  promotion_id: string | null;
  reason: string;
  source: "sale_window" | "regular_only" | "unsellable" | "invalid_promo_rejected";
  currency: "UAH";
}

/** @deprecated Prefer EffectivePriceResult — kept for existing UI call sites. */
export interface DisplayPriceResult {
  primary: number;
  regular: number | null;
  sale: number | null;
  saleActive: boolean;
  badge: string | null;
  savings: number | null;
  promotionId: string | null;
}

export function extractSaleFields(
  product: Partial<CatalogV2Product> | (Partial<CatalogV2Product> & Record<string, unknown>),
): SalePricingFields {
  return {
    regular_price:
      typeof product.regularPriceUah === "number"
        ? product.regularPriceUah
        : typeof product.priceUah === "number"
          ? product.priceUah
          : null,
    sale_price: typeof product.salePriceUah === "number" ? product.salePriceUah : null,
    sale_starts_at: typeof product.saleStartsAt === "string" ? product.saleStartsAt : null,
    sale_ends_at: typeof product.saleEndsAt === "string" ? product.saleEndsAt : null,
    promotion_id: typeof product.promotionId === "string" ? product.promotionId : null,
    promotion_status: typeof product.promotionStatus === "string" ? product.promotionStatus : null,
    price_updated_at: typeof product.priceUpdatedAt === "string" ? product.priceUpdatedAt : null,
    source_revision: typeof product.sourceRevision === "string" ? product.sourceRevision : null,
    currency: "UAH",
  };
}

function isPositiveMoney(n: number | null | undefined): n is number {
  return typeof n === "number" && Number.isFinite(n) && n > 0;
}

/** Reject fake discounts and out-of-window promotions. Missing end date = inactive (fail-closed). */
export function isSaleActive(fields: SalePricingFields, now = new Date()): boolean {
  if ((fields.promotion_status ?? "active") !== "active") return false;
  if (!isPositiveMoney(fields.regular_price) || !isPositiveMoney(fields.sale_price)) return false;
  if (!(fields.sale_price < fields.regular_price)) return false;
  if (!fields.sale_ends_at) return false;
  const starts = fields.sale_starts_at ? Date.parse(fields.sale_starts_at) : Number.NEGATIVE_INFINITY;
  const ends = Date.parse(fields.sale_ends_at);
  const ts = now.getTime();
  if (!Number.isFinite(starts) || !Number.isFinite(ends)) return false;
  return ts >= starts && ts < ends;
}

/**
 * Canonical backend-equivalent resolver for Store V2 surfaces.
 * Frontend must not invent promotions outside this result.
 */
export function resolveEffectivePrice(
  product: CatalogV2Product,
  now = new Date(),
): EffectivePriceResult {
  const fields = extractSaleFields(product);
  const fallback = product.priceUah;
  const regular =
    isPositiveMoney(fields.regular_price)
      ? fields.regular_price
      : isPositiveMoney(fallback)
        ? fallback
        : 0;

  const sellable = product.isSellable && product.isPriced && isPositiveMoney(regular);
  if (!sellable) {
    return {
      regular_price: regular,
      effective_price: regular,
      discount_amount: 0,
      discount_percent: 0,
      promotion_active: false,
      promotion_end_at: null,
      promotion_id: null,
      reason: "unsellable_or_unpriced",
      source: "unsellable",
      currency: "UAH",
    };
  }

  if (
    fields.sale_price != null &&
    isPositiveMoney(fields.sale_price) &&
    isPositiveMoney(fields.regular_price) &&
    !(fields.sale_price < fields.regular_price)
  ) {
    return {
      regular_price: regular,
      effective_price: regular,
      discount_amount: 0,
      discount_percent: 0,
      promotion_active: false,
      promotion_end_at: null,
      promotion_id: null,
      reason: "invalid_promo_rejected_sale_gte_regular",
      source: "invalid_promo_rejected",
      currency: "UAH",
    };
  }

  const active = isSaleActive(fields, now);
  if (!active || !isPositiveMoney(fields.sale_price)) {
    return {
      regular_price: regular,
      effective_price: regular,
      discount_amount: 0,
      discount_percent: 0,
      promotion_active: false,
      promotion_end_at: fields.sale_ends_at,
      promotion_id: null,
      reason: fields.sale_price != null ? "promotion_inactive_or_expired" : "regular_only",
      source: "regular_only",
      currency: "UAH",
    };
  }

  const discountAmount = Math.max(0, regular - fields.sale_price);
  const discountPercent = regular > 0 ? Math.round((discountAmount / regular) * 100) : 0;
  return {
    regular_price: regular,
    effective_price: fields.sale_price,
    discount_amount: discountAmount,
    discount_percent: discountPercent,
    promotion_active: true,
    promotion_end_at: fields.sale_ends_at,
    promotion_id: fields.promotion_id,
    reason: "active_sale_window",
    source: "sale_window",
    currency: "UAH",
  };
}

export function resolveDisplayPrice(
  product: CatalogV2Product,
  now = new Date(),
): DisplayPriceResult {
  const eff = resolveEffectivePrice(product, now);
  if (eff.source === "unsellable") {
    return {
      primary: product.priceUah ?? 0,
      regular: eff.regular_price || null,
      sale: null,
      saleActive: false,
      badge: null,
      savings: null,
      promotionId: null,
    };
  }
  if (!eff.promotion_active) {
    return {
      primary: eff.effective_price,
      regular: eff.regular_price,
      sale: null,
      saleActive: false,
      badge: null,
      savings: null,
      promotionId: null,
    };
  }
  return {
    primary: eff.effective_price,
    regular: eff.regular_price,
    sale: eff.effective_price,
    saleActive: true,
    badge: eff.discount_percent > 0 ? `−${eff.discount_percent}%` : "Sale",
    savings: eff.discount_amount,
    promotionId: eff.promotion_id,
  };
}
